// WebFilterProfileDetail.tsx
import { useState, useCallback, useEffect } from "react";
import {
    Loader2, Globe, Shield, Settings,
    Filter, AlertTriangle, Info, CheckCircle2, XCircle
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface FtgdFilter {
    id: number;
    log: string;
    action: string;
    category: number;
    "warn-duration": string;
    "warning-prompt": string;
}

interface FtgdWf {
    filters: FtgdFilter[];
    options: string;
    "exempt-quota": string;
    "rate-crl-urls": string;
    "rate-css-urls": string;
    "max-quota-timeout": number;
    "rate-javascript-urls": string;
}

interface WebSettings {
    blocklist: string;
    "log-search": string;
    "safe-search": string;
    "bword-threshold": number;
    "youtube-restrict": string;
}

interface Override {
    "ovrd-dur": string;
    "ovrd-scope": string;
    "ovrd-cookie": string;
    "profile-type": string;
    "ovrd-dur-mode": string;
}

interface Antiphish {
    status: string;
    "check-uri": string;
    "max-body-len": number;
    authentication: string;
    "default-action": string;
    "check-basic-auth": string;
    "check-username-only": string;
}

interface WebFilterProfile {
    id: number;
    name: string;
    comment: string;
    feature_set: string;
    options: string;
    https_replacemsg: string;
    web_flow_log_encoding: string;
    post_action: string;
    wisp: string;
    wisp_algorithm: string;
    log_all_url: string;
    web_content_log: string;
    extended_log: string;
    web_antiphishing_log: string;
    web_filter_activex_log: string;
    web_filter_applet_log: string;
    web_filter_command_block_log: string;
    web_filter_cookie_log: string;
    web_filter_js_log: string;
    web_filter_referer_log: string;
    web_filter_unknown_log: string;
    web_filter_vbs_log: string;
    web_ftgd_err_log: string;
    web_ftgd_quota_usage: string;
    web_url_log: string;
    web_invalid_domain_log: string;
    ftgd_wf: string;
    web: string;
    override: string;
    antiphish: string;
    web_filter_jscript_log: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseJson = <T,>(raw: string, fallback: T): T => {
    try { return JSON.parse(raw) as T; } catch { return fallback; }
};

// ── Chips ─────────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | undefined }) => {
    const str = String(val ?? "");
    if (!str) return <span className="text-gray-200 text-[9px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable"
            ? "bg-green-500/15 border-green-500/30 text-green-400"
            : lower === "disable"
                ? "bg-gray-700/50 border-gray-600/40 text-gray-200/50"
                : lower === "block"
                    ? "bg-red-500/15 border-red-500/30 text-red-400"
                    : lower === "monitor"
                        ? "bg-cyan-500/15 border-cyan-500/30 text-cyan-400"
                        : lower === "allow"
                            ? "bg-green-500/15 border-green-500/30 text-green-400"
                            : lower === "warn"
                                ? "bg-yellow-500/15 border-yellow-500/30 text-yellow-400"
                                : "bg-indigo-500/15 border-indigo-500/30 text-indigo-300";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase whitespace-nowrap ${style}`}>
            {str}
        </span>
    );
};

const BoolIcon = ({ val }: { val: string }) => {
    const on = val === "enable";
    return on
        ? <CheckCircle2 size={12} className="text-green-400 shrink-0" />
        : <XCircle size={12} className="text-gray-200/30 shrink-0" />;
};

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, label, color, badge }: {
    icon: any; label: string; color: string; badge?: string | number;
}) => (
    <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-gray-800">
        <div className={`p-1.5 rounded-lg border ${color}`}>
            <Icon size={13} className={color.includes("blue") ? "text-blue-400" : color.includes("green") ? "text-green-400" : color.includes("violet") ? "text-violet-400" : color.includes("orange") ? "text-orange-400" : color.includes("cyan") ? "text-cyan-400" : "text-yellow-400"} />
        </div>
        <h3 className="text-[12px] font-black text-gray-200 uppercase tracking-wider">{label}</h3>
        {badge !== undefined && (
            <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold border ${color}`}>
                {badge}
            </span>
        )}
    </div>
);

// ── Row ───────────────────────────────────────────────────────────────────────
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-800/60 last:border-0">
        <span className="text-[10px] font-mono text-gray-200 shrink-0">{label}</span>
        <div className="ml-3 text-right">{children}</div>
    </div>
);

// ── Log Toggle Row ────────────────────────────────────────────────────────────
const LogRow = ({ label, val }: { label: string; val: string }) => (
    <div className="flex items-center gap-2 py-1.5 border-b border-gray-800/40 last:border-0">
        <BoolIcon val={val} />
        <span className={`text-[10px] font-mono ${val === "enable" ? "text-gray-200" : "text-gray-200"}`}>{label}</span>
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function WebFilterProfileDetail({
    asset_id,
    client_id,
    profile_name

}: {
    asset_id: any;
    client_id: any;
    profile_name?: string;
}) {
    const [profile, setProfile] = useState<WebFilterProfile | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_web_filter_profiles", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        name: profile_name,
                    }),

                },
            });
            if (res.data.status) {
                setProfile(res.data.data?.[0] ?? null);
            } else showError("Failed to load web filter profile");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200/50">Loading web filter profile...</span>
        </div>
    );

    if (!profile) return (
        <div className="flex flex-col items-center gap-2 py-16 opacity-40">
            <Globe size={22} className="text-gray-200" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200/50">No profile found</span>
        </div>
    );

    const ftgd = parseJson<FtgdWf>(profile.ftgd_wf, { filters: [], options: "", "exempt-quota": "", "rate-crl-urls": "", "rate-css-urls": "", "max-quota-timeout": 0, "rate-javascript-urls": "" });
    const web = parseJson<WebSettings>(profile.web, {} as WebSettings);
    const override = parseJson<Override>(profile.override, {} as Override);
    const antiphish = parseJson<Antiphish>(profile.antiphish, {} as Antiphish);

    const blockCount = ftgd.filters.filter(f => f.action === "block").length;
    const monitorCount = ftgd.filters.filter(f => f.action === "monitor").length;
    const warnCount = ftgd.filters.filter(f => f.action === "warn").length;

    return (
        <div className="space-y-4">

            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-blue-500/10 border border-blue-500/20 p-2 rounded-xl">
                        <Globe size={18} className="text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-[18px] font-black text-gray-200">{profile.name}</h1>
                        {profile.comment && (
                            <p className="text-[11px] text-gray-200/50 mt-0.5">{profile.comment}</p>
                        )}
                    </div>
                    <div className="flex gap-2 ml-2">
                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-blue-500/10 border border-blue-500/20 text-blue-400 uppercase">
                            {profile.feature_set}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-gray-700/50 border border-gray-600/40 text-gray-200/70 uppercase">
                            {profile.options || "no options"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: "Total Filters", value: ftgd.filters.length, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
                    { label: "Blocked", value: blockCount, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
                    { label: "Monitor", value: monitorCount, color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/20" },
                    { label: "Warn", value: warnCount, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/20" },
                ].map(({ label, value, color, bg }) => (
                    <div key={label} className={`rounded-xl border px-4 py-3 ${bg}`}>
                        <p className="text-[9px] font-bold text-gray-200/50 uppercase tracking-widest mb-1">{label}</p>
                        <p className={`text-[26px] font-black leading-none ${color}`}>{value}</p>
                    </div>
                ))}
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* General Settings */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4">
                    <SectionHeader icon={Settings} label="General Settings" color="bg-blue-500/10 border-blue-500/20" />
                    <Row label="HTTPS Replace Msg"><ValChip val={profile.https_replacemsg} /></Row>
                    <Row label="Flow Log Encoding">
                        <span className="text-[10px] font-mono text-gray-200">{profile.web_flow_log_encoding}</span>
                    </Row>
                    <Row label="Post Action">
                        <span className="text-[10px] font-mono text-gray-200">{profile.post_action}</span>
                    </Row>
                    <Row label="WISP"><ValChip val={profile.wisp} /></Row>
                    <Row label="WISP Algorithm">
                        <span className="text-[10px] font-mono text-gray-200">{profile.wisp_algorithm}</span>
                    </Row>
                    <Row label="Log All URLs"><ValChip val={profile.log_all_url} /></Row>
                    <Row label="Extended Log"><ValChip val={profile.extended_log} /></Row>
                    <div className="mt-3 pt-3 border-t border-gray-800">
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Web Settings</p>
                        <Row label="Blocklist"><ValChip val={web.blocklist} /></Row>
                        <Row label="Log Search"><ValChip val={web["log-search"]} /></Row>
                        <Row label="Safe Search">
                            <span className="text-[10px] text-gray-200">{web["safe-search"] || "—"}</span>
                        </Row>
                        <Row label="YouTube Restrict">
                            <span className="text-[10px] font-mono text-gray-200">{web["youtube-restrict"] || "none"}</span>
                        </Row>
                        <Row label="Bad Word Threshold">
                            <span className="text-[10px] font-mono text-gray-200">{web["bword-threshold"]}</span>
                        </Row>
                    </div>
                </div>

                {/* Log Settings */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4">
                    <SectionHeader icon={Info} label="Log Settings" color="bg-violet-500/10 border-violet-500/20" />
                    <LogRow label="Content Log" val={profile.web_content_log} />
                    <LogRow label="URL Log" val={profile.web_url_log} />
                    <LogRow label="Anti-Phishing Log" val={profile.web_antiphishing_log} />
                    <LogRow label="ActiveX Log" val={profile.web_filter_activex_log} />
                    <LogRow label="Applet Log" val={profile.web_filter_applet_log} />
                    <LogRow label="Command Block Log" val={profile.web_filter_command_block_log} />
                    <LogRow label="Cookie Log" val={profile.web_filter_cookie_log} />
                    <LogRow label="JS Log" val={profile.web_filter_js_log} />
                    <LogRow label="JScript Log" val={profile.web_filter_jscript_log} />
                    <LogRow label="Referer Log" val={profile.web_filter_referer_log} />
                    <LogRow label="Unknown Log" val={profile.web_filter_unknown_log} />
                    <LogRow label="VBS Log" val={profile.web_filter_vbs_log} />
                    <LogRow label="FTGD Error Log" val={profile.web_ftgd_err_log} />
                    <LogRow label="FTGD Quota Usage" val={profile.web_ftgd_quota_usage} />
                    <LogRow label="Invalid Domain Log" val={profile.web_invalid_domain_log} />

                    {/* Override Settings */}
                    <div className="mt-3 pt-3 border-t border-gray-800">
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-2">Override Settings</p>
                        <Row label="Duration"><span className="text-[10px] font-mono text-gray-200">{override["ovrd-dur"]}</span></Row>
                        <Row label="Scope"><span className="text-[10px] font-mono text-gray-200">{override["ovrd-scope"]}</span></Row>
                        <Row label="Cookie"><span className="text-[10px] font-mono text-gray-200">{override["ovrd-cookie"]}</span></Row>
                        <Row label="Duration Mode"><span className="text-[10px] font-mono text-gray-200">{override["ovrd-dur-mode"]}</span></Row>
                        <Row label="Profile Type"><span className="text-[10px] font-mono text-gray-200">{override["profile-type"]}</span></Row>
                    </div>
                </div>

                {/* Right column: FTGD + Antiphish */}
                <div className="space-y-4">

                    {/* FTGD Settings */}
                    <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4">
                        <SectionHeader icon={Filter} label="FTGD Settings" color="bg-orange-500/10 border-orange-500/20" />
                        <Row label="Exempt Quota">
                            <span className="text-[10px] font-mono text-gray-200">{ftgd["exempt-quota"] || "—"}</span>
                        </Row>
                        <Row label="Max Quota Timeout">
                            <span className="text-[10px] font-mono text-gray-200">{ftgd["max-quota-timeout"]}s</span>
                        </Row>
                        <Row label="Rate CRL URLs"><ValChip val={ftgd["rate-crl-urls"]} /></Row>
                        <Row label="Rate CSS URLs"><ValChip val={ftgd["rate-css-urls"]} /></Row>
                        <Row label="Rate JS URLs"><ValChip val={ftgd["rate-javascript-urls"]} /></Row>
                    </div>

                    {/* Anti-Phishing */}
                    <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4">
                        <SectionHeader icon={AlertTriangle} label="Anti-Phishing" color="bg-yellow-500/10 border-yellow-500/20" />
                        <Row label="Status"><ValChip val={antiphish.status} /></Row>
                        <Row label="Check URI"><ValChip val={antiphish["check-uri"]} /></Row>
                        <Row label="Check Basic Auth"><ValChip val={antiphish["check-basic-auth"]} /></Row>
                        <Row label="Check Username Only"><ValChip val={antiphish["check-username-only"]} /></Row>
                        <Row label="Default Action">
                            <span className="text-[10px] font-mono text-gray-200">{antiphish["default-action"]}</span>
                        </Row>
                        <Row label="Authentication">
                            <span className="text-[10px] font-mono text-gray-200">{antiphish.authentication}</span>
                        </Row>
                        <Row label="Max Body Len">
                            <span className="text-[10px] font-mono text-gray-200">{antiphish["max-body-len"]} KB</span>
                        </Row>
                    </div>
                </div>
            </div>

            {/* FTGD Category Filters */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4">
                <SectionHeader
                    icon={Shield}
                    label="FTGD Category Filters"
                    color="bg-cyan-500/10 border-cyan-500/20"
                    badge={`${ftgd.filters.length} rules`}
                />
                <div className="flex flex-wrap gap-1.5">
                    {ftgd.filters.map(f => {
                        const isBlock = f.action === "block";
                        const isMonitor = f.action === "monitor";
                        const isWarn = f.action === "warn";
                        const style = isBlock
                            ? "bg-red-500/10 border-red-500/25 text-red-400"
                            : isMonitor
                                ? "bg-cyan-500/10 border-cyan-500/25 text-cyan-400"
                                : isWarn
                                    ? "bg-yellow-500/10 border-yellow-500/25 text-yellow-400"
                                    : "bg-gray-700/40 border-gray-600/30 text-gray-200/70";
                        return (
                            <div
                                key={f.id}
                                title={`Category ${f.category} · ${f.action} · log: ${f.log}`}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[9px] font-bold cursor-default ${style}`}
                            >
                                {/* <Hash size={7} /> */}
                                <span className="font-mono">Cat {f.category}</span>
                                {/* <span className="opacity-60 uppercase text-[7px]">{f.action}</span> */}
                            </div>
                        );
                    })}
                </div>
                {/* Legend */}
                <div className="flex items-center gap-4 mt-3 pt-2 border-t border-gray-800">
                    {[
                        { color: "bg-red-500", label: "Block" },
                        { color: "bg-cyan-500", label: "Monitor" },
                        { color: "bg-yellow-500", label: "Warn" },
                    ].map(({ color, label }) => (
                        <div key={label} className="flex items-center gap-1.5">
                            <div className={`w-2.5 h-2.5 rounded-sm ${color}`} />
                            <span className="text-[9px] text-gray-200 font-bold uppercase tracking-widest">{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
